/**
 * TimelinePage — Ligne du temps interactive
 *
 * Combine transits majeurs (planètes lentes) et événements de vie annotés.
 * Timeline verticale avec colonne gauche (astro) et droite (vie).
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import { useClient } from '../contexts/ClientContext.js';

interface EvenementVie {
  id: number;
  date: string;
  titre: string;
  description: string | null;
  categorie: string | null;
}

interface TransitAspect {
  date: string;
  planeteTransit: string;
  aspect: string;
  pointNatal: string;
  signe: string;
  direction: string;
}

interface TransitsData {
  aspects: TransitAspect[];
}

const PLANETES_LENTES = new Set(['Saturne', 'Uranus', 'Neptune', 'Pluton']);

const PLANET_SYMBOLS: Record<string, string> = {
  Saturne: '♄', Uranus: '♅', Neptune: '♆', Pluton: '♇',
};

const ASPECT_COLORS: Record<string, { bg: string; text: string }> = {
  Conjonction: { bg: '#fefce8', text: '#854d0e' },
  Opposition: { bg: '#fef2f2', text: '#991b1b' },
  'Carré': { bg: '#fef2f2', text: '#991b1b' },
  Trigone: { bg: '#f0fdf4', text: '#166534' },
  Sextile: { bg: '#f0fdf4', text: '#166534' },
};

const CATEGORIES = [
  { value: 'professionnel', label: 'Professionnel', color: '#2563eb' },
  { value: 'familial', label: 'Familial', color: '#16a34a' },
  { value: 'santé', label: 'Santé', color: '#dc2626' },
  { value: 'relationnel', label: 'Relationnel', color: '#d946ef' },
  { value: 'autre', label: 'Autre', color: '#6b7280' },
];

function getCatColor(cat: string | null): string {
  return CATEGORIES.find((c) => c.value === cat)?.color ?? '#6b7280';
}

function parseFRDate(dateStr: string): Date {
  // DD/MM/YYYY → Date
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
  }
  return new Date(dateStr);
}

function formatDateFR(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

interface TimelineItem {
  date: Date;
  dateStr: string;
  type: 'transit' | 'evenement';
  transit?: TransitAspect;
  evenement?: EvenementVie;
}

export function TimelinePage() {
  const { id } = useParams<{ id: string }>();
  const { client } = useClient();

  const [evenements, setEvenements] = useState<EvenementVie[]>([]);
  const [transits, setTransits] = useState<TransitAspect[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [filterPlanetes, setFilterPlanetes] = useState<Set<string>>(new Set(PLANETES_LENTES));
  const [filterCategories, setFilterCategories] = useState<Set<string>>(new Set(CATEGORIES.map((c) => c.value)));

  // Formulaire nouvel événement
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState('');
  const [formTitre, setFormTitre] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCat, setFormCat] = useState('autre');

  // Charger les données
  useEffect(() => {
    if (!id || !client) return;

    const promises: Promise<void>[] = [];

    // Événements de vie
    promises.push(
      apiFetch<EvenementVie[]>(`/clients/${id}/evenements`)
        .then((res) => { if (res.success && res.data) setEvenements(res.data); })
    );

    // Transits (planètes lentes seulement)
    const transitChart = client.charts
      .filter((c) => c.type === 'TRANSITS')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (transitChart) {
      promises.push(
        apiFetch<TransitsData>(`/clients/${id}/charts/${transitChart.id}/transits?exclureLune=true`)
          .then((res) => {
            if (res.success && res.data) {
              // Filtrer côté client : uniquement planètes lentes
              const lents = res.data.aspects.filter((a) => PLANETES_LENTES.has(a.planeteTransit));
              setTransits(lents);
            }
          })
      );
    }

    Promise.all(promises).finally(() => setLoading(false));
  }, [id, client]);

  // Combiner et trier les items
  const timelineItems = useMemo(() => {
    const items: TimelineItem[] = [];

    for (const t of transits) {
      if (!filterPlanetes.has(t.planeteTransit)) continue;
      const d = parseFRDate(t.date);
      items.push({ date: d, dateStr: t.date, type: 'transit', transit: t });
    }

    for (const e of evenements) {
      if (e.categorie && !filterCategories.has(e.categorie)) continue;
      const d = new Date(e.date);
      items.push({ date: d, dateStr: formatDateFR(d), type: 'evenement', evenement: e });
    }

    items.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Dédupliquer les transits par planète/aspect/point par mois
    const seen = new Set<string>();
    return items.filter((item) => {
      if (item.type === 'evenement') return true;
      const t = item.transit!;
      const month = `${item.date.getFullYear()}-${item.date.getMonth()}`;
      const key = `${t.planeteTransit}-${t.aspect}-${t.pointNatal}-${month}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [transits, evenements, filterPlanetes, filterCategories]);

  // Vérifier coïncidences (±30 jours)
  const coincidences = useMemo(() => {
    const evtDates = evenements.map((e) => ({ id: e.id, time: new Date(e.date).getTime() }));
    const transitDates = transits.map((t) => ({ date: t.date, time: parseFRDate(t.date).getTime() }));
    const matches = new Set<number>();

    for (const evt of evtDates) {
      for (const tr of transitDates) {
        if (Math.abs(evt.time - tr.time) <= 30 * 24 * 60 * 60 * 1000) {
          matches.add(evt.id);
          break;
        }
      }
    }
    return matches;
  }, [evenements, transits]);

  async function handleAddEvent() {
    if (!formDate || !formTitre) return;

    const res = await apiFetch<EvenementVie>(`/clients/${id}/evenements`, {
      method: 'POST',
      body: JSON.stringify({ date: formDate, titre: formTitre, description: formDesc || null, categorie: formCat }),
    });

    if (res.success && res.data) {
      setEvenements((prev) => [...prev, res.data!]);
      setShowForm(false);
      setFormDate('');
      setFormTitre('');
      setFormDesc('');
    }
  }

  async function handleDeleteEvent(evtId: number) {
    if (!confirm('Supprimer cet événement ?')) return;
    const res = await apiFetch(`/clients/${id}/evenements/${evtId}`, { method: 'DELETE' });
    if (res.success) {
      setEvenements((prev) => prev.filter((e) => e.id !== evtId));
    }
  }

  function toggleSet(set: Set<string>, value: string, setter: (s: Set<string>) => void) {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        Construction de la timeline...
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: colors.text }}>
          <i className="bi bi-calendar3" style={{ color: '#d946ef', marginRight: 8 }} />
          Timeline
        </h1>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: '#d946ef', color: '#fff', fontSize: '0.85rem',
            fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-plus-lg" style={{ marginRight: 4 }} />
          Événement
        </button>
      </div>

      {/* Formulaire nouvel événement */}
      {showForm && (
        <div style={{
          background: '#fff', borderRadius: 8, padding: '1rem',
          marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.75rem', color: colors.textMid, display: 'block', marginBottom: 2 }}>Date</label>
              <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: `1px solid ${colors.border}`, fontFamily: 'inherit' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: colors.textMid, display: 'block', marginBottom: 2 }}>Catégorie</label>
              <select value={formCat} onChange={(e) => setFormCat(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: `1px solid ${colors.border}`, fontFamily: 'inherit' }}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <input value={formTitre} onChange={(e) => setFormTitre(e.target.value)} placeholder="Titre"
            style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: `1px solid ${colors.border}`, marginBottom: '0.5rem', fontFamily: 'inherit' }} />
          <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Description (optionnel)"
            rows={2} style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: `1px solid ${colors.border}`, marginBottom: '0.75rem', fontFamily: 'inherit', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleAddEvent}
              style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: '#d946ef', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>
              Ajouter
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '6px 16px', borderRadius: 4, border: `1px solid ${colors.border}`, background: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: '0.75rem 1rem',
        marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.8rem',
      }}>
        <div>
          <span style={{ fontWeight: 600, color: colors.textMid, marginRight: 8 }}>Planètes :</span>
          {[...PLANETES_LENTES].map((p) => (
            <button key={p} onClick={() => toggleSet(filterPlanetes, p, setFilterPlanetes)}
              style={{
                padding: '2px 8px', borderRadius: 4, marginRight: 4, cursor: 'pointer',
                border: 'none', fontFamily: 'inherit', fontSize: '0.75rem',
                background: filterPlanetes.has(p) ? '#ede9fe' : '#f5f5f5',
                color: filterPlanetes.has(p) ? '#5b21b6' : '#999',
              }}>
              {PLANET_SYMBOLS[p]} {p}
            </button>
          ))}
        </div>
        <div>
          <span style={{ fontWeight: 600, color: colors.textMid, marginRight: 8 }}>Vie :</span>
          {CATEGORIES.map((c) => (
            <button key={c.value} onClick={() => toggleSet(filterCategories, c.value, setFilterCategories)}
              style={{
                padding: '2px 8px', borderRadius: 4, marginRight: 4, cursor: 'pointer',
                border: 'none', fontFamily: 'inherit', fontSize: '0.75rem',
                background: filterCategories.has(c.value) ? `${c.color}20` : '#f5f5f5',
                color: filterCategories.has(c.value) ? c.color : '#999',
              }}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      {timelineItems.length === 0 ? (
        <div style={{
          background: '#fff', borderRadius: 8, padding: '3rem',
          textAlign: 'center', color: colors.textMuted, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          Aucun élément à afficher. Importez des transits ou ajoutez des événements.
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          {/* Ligne verticale centrale */}
          <div style={{
            position: 'absolute', left: 9, top: 0, bottom: 0,
            width: 2, background: colors.border,
          }} />

          {timelineItems.map((item, i) => {
            const isEvt = item.type === 'evenement';
            const highlight = isEvt && item.evenement && coincidences.has(item.evenement.id);

            return (
              <div key={`${item.type}-${i}`} style={{
                position: 'relative', marginBottom: '0.75rem', paddingLeft: 24,
              }}>
                {/* Point sur la timeline */}
                <div style={{
                  position: 'absolute', left: 4, top: 8,
                  width: 12, height: 12, borderRadius: '50%',
                  background: isEvt ? getCatColor(item.evenement?.categorie ?? null) : '#7c3aed',
                  border: highlight ? '2px solid #fbbf24' : '2px solid #fff',
                  boxShadow: '0 0 0 1px ' + colors.border,
                }} />

                <div style={{
                  background: highlight ? '#fffbeb' : '#fff',
                  borderRadius: 6, padding: '0.5rem 0.75rem',
                  border: highlight ? '1px solid #fde68a' : '1px solid transparent',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginBottom: 2 }}>
                    {item.dateStr}
                    {highlight && (
                      <span style={{ marginLeft: 6, color: '#b45309', fontWeight: 600 }}>
                        ✦ Coïncidence transit
                      </span>
                    )}
                  </div>

                  {isEvt && item.evenement ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{
                        background: getCatColor(item.evenement.categorie) + '20',
                        color: getCatColor(item.evenement.categorie),
                        padding: '1px 6px', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600,
                      }}>
                        {item.evenement.categorie ?? 'autre'}
                      </span>
                      <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>{item.evenement.titre}</span>
                      <i
                        className="bi bi-trash"
                        onClick={() => handleDeleteEvent(item.evenement!.id)}
                        style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.7rem', marginLeft: 'auto' }}
                        title="Supprimer"
                      />
                    </div>
                  ) : item.transit ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
                      <span style={{ fontWeight: 600 }}>
                        {PLANET_SYMBOLS[item.transit.planeteTransit] ?? ''} {item.transit.planeteTransit}
                      </span>
                      {(() => {
                        const ac = ASPECT_COLORS[item.transit!.aspect] ?? { bg: '#f5f5f5', text: '#666' };
                        return (
                          <span style={{ background: ac.bg, color: ac.text, padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem' }}>
                            {item.transit!.aspect}
                          </span>
                        );
                      })()}
                      <span>{item.transit.pointNatal}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
